import React, { useState, useMemo } from 'react';
import { trpc } from '@/lib/trpc';
import {
  ComposedChart,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from 'recharts';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  Gauge,
  LineChart,
  Plus,
  ShieldAlert,
  TrendingUp,
  Zap,
  Check,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import ExerciseProgressionDialog from './ExerciseProgressionDialog';

// ─── Muscle group color palette ───────────────────────────────────────────────
const MUSCLE_COLORS: Record<string, string> = {
  Chest: '#22c55e',
  Back: '#3b82f6',
  Legs: '#f59e0b',
  Shoulders: '#a855f7',
  Arms: '#ef4444',
  Core: '#06b6d4',
};

// ─── Custom bar tooltip ───────────────────────────────────────────────────────
const VolumeTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono shadow-lg">
      <div className="text-muted-foreground mb-1">{label}</div>
      <div className="text-foreground font-bold">
        {payload[0].value.toLocaleString()} <span className="text-muted-foreground font-normal">LBS</span>
      </div>
    </div>
  );
};

const ShadowVolumeTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  const actual = d?.volumeLbs ?? 0;
  const target = d?.shadowTarget ?? 0;
  const pct = target > 0 ? Math.round((actual / target) * 100) : 0;
  const onTarget = actual >= target;
  return (
    <div className="bg-background border border-border rounded-sm px-3 py-2.5 text-xs font-mono shadow-xl space-y-1.5">
      <div className="text-muted-foreground font-bold uppercase">{label}</div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Actual</span>
        <span className="text-foreground font-bold">{actual.toLocaleString()} LBS</span>
      </div>
      <div className="flex items-center justify-between gap-4">
        <span className="text-muted-foreground">Shadow Target</span>
        <span className="text-muted-foreground">{target.toLocaleString()} LBS</span>
      </div>
      <div className={`flex items-center justify-between gap-4 pt-1 border-t border-border/40`}>
        <span className="text-muted-foreground">vs Target</span>
        <span className={`font-bold ${onTarget ? 'text-green-400' : 'text-amber-400'}`}>
          {pct}% {onTarget ? '✓' : '↓'}
        </span>
      </div>
      {d?.isCurrentWeek && (
        <div className="text-[9px] text-primary uppercase font-bold pt-0.5">← Current Week</div>
      )}
    </div>
  );
};

const MuscleTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-background border border-border rounded-sm px-3 py-2 text-xs font-mono shadow-lg">
      <div className="text-muted-foreground mb-1">{label}</div>
      <div className="text-foreground font-bold">
        {payload[0].value} <span className="text-muted-foreground font-normal">SETS</span>
      </div>
    </div>
  );
};

// ─── Props ────────────────────────────────────────────────────────────────────
interface TelemetryMesocycleModeProps {
  refreshTrigger: number;
  onLoadTemplate?: (template: any) => void;
}

export default function TelemetryMesocycleMode({ refreshTrigger, onLoadTemplate }: TelemetryMesocycleModeProps) {
  const [blueprintsOpen, setBlueprintsOpen] = useState(false);
  const [isCreatingTemplate, setIsCreatingTemplate] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateCategory, setNewTemplateCategory] = useState<'Push' | 'Pull' | 'Legs' | 'Core/Cardio' | 'Other'>('Push');
  const [selectedLifts, setSelectedLifts] = useState<{ exerciseId: string; targetSetsCount: number }[]>([]);
  const [tempSetsCount, setTempSetsCount] = useState<Record<string, number>>({});

  // Progression dialog
  const [progressionExerciseId, setProgressionExerciseId] = useState<string | null>(null);
  const [progressionExerciseName, setProgressionExerciseName] = useState('');
  const [progressionInitialMode, setProgressionInitialMode] = useState<'overload' | 'hypertrophy'>('overload');

  const utils = trpc.useUtils();

  const { data: mesoData, isLoading: mesoLoading } = trpc.telemetry.getMesocycleData.useQuery(undefined, {
    staleTime: 60_000,
  });
  const { data: rawTemplates = [] } = trpc.templates.list.useQuery();
  const { data: rawExercises = [] } = trpc.exercises.list.useQuery();

  const saveTemplateMutation = trpc.templates.save.useMutation({
    onSuccess: () => utils.templates.list.invalidate(),
  });
  const deleteTemplateMutation = trpc.templates.delete.useMutation({
    onSuccess: () => utils.templates.list.invalidate(),
  });

  const templates = rawTemplates.map((t) => ({
    id: t.id,
    name: t.name,
    category: (t.category as any) || 'Other',
    exercises: (t.exercises as any[]) || [],
  }));

  const exercises = rawExercises.map((e) => ({
    id: e.id,
    name: e.name,
    category: e.category,
    trackingMode: e.trackingMode,
  }));

  const handleCreateTemplate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTemplateName.trim()) { toast.error('Please enter a template name'); return; }
    if (selectedLifts.length === 0) { toast.error('Please select at least one lift'); return; }
    await saveTemplateMutation.mutateAsync({
      name: newTemplateName.trim(),
      category: newTemplateCategory,
      exercises: selectedLifts,
    });
    toast.success(`Blueprint "${newTemplateName}" saved!`);
    setNewTemplateName('');
    setSelectedLifts([]);
    setIsCreatingTemplate(false);
  };

  const handleDeleteTemplate = async (id: string, name: string) => {
    if (confirm(`Delete template "${name}"?`)) {
      await deleteTemplateMutation.mutateAsync({ templateId: id });
      toast.success(`Deleted "${name}"`);
    }
  };

  const handleToggleLift = (exerciseId: string) => {
    const exists = selectedLifts.some((l) => l.exerciseId === exerciseId);
    if (exists) {
      setSelectedLifts(selectedLifts.filter((l) => l.exerciseId !== exerciseId));
    } else {
      setSelectedLifts([...selectedLifts, { exerciseId, targetSetsCount: tempSetsCount[exerciseId] || 3 }]);
    }
  };

  const handleSetsChange = (exerciseId: string, count: number) => {
    setTempSetsCount({ ...tempSetsCount, [exerciseId]: count });
    setSelectedLifts(selectedLifts.map((l) => l.exerciseId === exerciseId ? { ...l, targetSetsCount: count } : l));
  };

  if (mesoLoading) {
    return (
      <div className="space-y-4 animate-pulse">
        {[1, 2, 3].map((i) => (
          <div key={i} className="forge-panel p-4 h-24 bg-secondary/20" />
        ))}
      </div>
    );
  }

  if (!mesoData) return null;

  const {
    currentWeekNum,
    totalWeeks,
    phaseLabel,
    blockCompletionPct,
    weeklyVolume,
    weekDeltaPct,
    liveWeekTarget,
    liveWeekActual,
    liveWeekPct,
    shadowStatus,
    shadowNote,
    setsPerMuscle,
    plateauCards,
    fatigueStatus,
    fatigueTitle,
    fatigueBody,
  } = mesoData;

  const fatigueColor = fatigueStatus === 'green'
    ? 'text-green-400 border-green-500/30 bg-green-500/10'
    : fatigueStatus === 'amber'
    ? 'text-amber-400 border-amber-500/30 bg-amber-500/10'
    : 'text-red-400 border-red-500/30 bg-red-500/10';

  const fatigueIcon = fatigueStatus === 'green'
    ? <CheckCircle2 className="h-5 w-5 text-green-400 shrink-0" />
    : fatigueStatus === 'amber'
    ? <AlertTriangle className="h-5 w-5 text-amber-400 shrink-0" />
    : <ShieldAlert className="h-5 w-5 text-red-400 shrink-0" />;

  return (
    <div className="space-y-5">

      {/* ── 1. Block Progress Bar ─────────────────────────────────────────────── */}
      <div className="forge-panel p-4 space-y-3">
        <div className="forge-header">
          <span>Current Block</span>
          <span className="text-primary font-mono flex items-center gap-1 text-xs">
            <Gauge className="h-3.5 w-3.5" /> MESOCYCLE
          </span>
        </div>

        <div className="flex items-center justify-between">
          <div>
            <div className="font-mono text-xl font-bold text-foreground">
              Week {currentWeekNum} <span className="text-muted-foreground text-sm font-normal">of {totalWeeks}</span>
            </div>
            <div className="text-xs font-mono text-primary uppercase tracking-wider mt-0.5">{phaseLabel}</div>
          </div>
          <div className="text-right">
            <div className="font-mono text-3xl font-bold text-primary">{blockCompletionPct}%</div>
            <div className="text-[10px] font-mono text-muted-foreground uppercase">Block Complete</div>
          </div>
        </div>

        {/* Progress bar */}
        <div className="relative h-3 bg-secondary/60 rounded-full overflow-hidden">
          <div
            className="absolute inset-y-0 left-0 bg-primary rounded-full transition-all duration-700 ease-out"
            style={{ width: `${blockCompletionPct}%` }}
          />
          {/* Week markers */}
          {[1, 2, 3].map((w) => (
            <div
              key={w}
              className="absolute inset-y-0 w-px bg-background/40"
              style={{ left: `${(w / totalWeeks) * 100}%` }}
            />
          ))}
        </div>

        <div className="flex justify-between text-[9px] font-mono text-muted-foreground uppercase">
          {Array.from({ length: totalWeeks }, (_, i) => (
            <span key={i} className={i + 1 === currentWeekNum ? 'text-primary font-bold' : ''}>
              WK {i + 1}
            </span>
          ))}
        </div>
      </div>

      {/* ── 2. Volume Progression + Shadow Line System ───────────────────────── */}
      <div className="forge-panel p-4 space-y-3">
        <div className="forge-header">
          <span>Volume Progression</span>
          <div className="flex items-center gap-2">
            {/* WoW badge: only on completed weeks */}
            {weekDeltaPct !== null && (
              <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded-sm border ${
                weekDeltaPct > 0
                  ? 'text-green-400 bg-green-500/10 border-green-500/30'
                  : weekDeltaPct < 0
                  ? 'text-red-400 bg-red-500/10 border-red-500/30'
                  : 'text-muted-foreground bg-secondary/30 border-border/40'
              }`}>
                {weekDeltaPct > 0 ? '+' : ''}{weekDeltaPct}% WoW
              </span>
            )}
            {/* Live target tracker for current in-progress week */}
            {liveWeekTarget > 0 && (
              <span className={`font-mono text-xs font-bold px-2 py-0.5 rounded-sm border ${
                liveWeekPct >= 100
                  ? 'text-green-400 bg-green-500/10 border-green-500/30'
                  : liveWeekPct >= 75
                  ? 'text-primary bg-primary/10 border-primary/30'
                  : 'text-amber-400 bg-amber-500/10 border-amber-500/30'
              }`}>
                {liveWeekPct}% of target
              </span>
            )}
            <span className="text-primary font-mono flex items-center gap-1 text-xs">
              <TrendingUp className="h-3.5 w-3.5" /> WEEKLY
            </span>
          </div>
        </div>

        {/* Live week progress detail */}
        {liveWeekTarget > 0 && (
          <div className="text-[10px] font-mono text-muted-foreground">
            <span className="text-foreground font-bold">{liveWeekActual.toLocaleString()}</span>
            {' / '}
            <span>{liveWeekTarget.toLocaleString()} LBS this week</span>
            <span className="ml-2 text-muted-foreground/60">— {liveWeekPct}% of shadow target</span>
          </div>
        )}

        <div className="text-[10px] font-mono text-muted-foreground uppercase">Total Volume (lbs) per Week · dashed = shadow target</div>

        {weeklyVolume.every((w) => w.volumeLbs === 0) ? (
          <div className="h-[160px] flex flex-col items-center justify-center border border-dashed border-border/40 rounded-sm">
            <Activity className="h-6 w-6 text-muted-foreground/30 mb-2" />
            <p className="text-xs font-mono text-muted-foreground uppercase">No sessions logged this block yet</p>
            <p className="text-[10px] text-muted-foreground/60 mt-1">Shadow targets will appear once you log your first session</p>
          </div>
        ) : (
          <div className="h-[180px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <ComposedChart data={weeklyVolume} margin={{ top: 12, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : String(v)}
                />
                <Tooltip content={<ShadowVolumeTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />

                {/* Actual volume bars — green if on/above target, lighter if below */}
                <Bar dataKey="volumeLbs" radius={[3, 3, 0, 0]} maxBarSize={56}>
                  {weeklyVolume.map((entry, index) => {
                    const onTarget = entry.volumeLbs >= entry.shadowTarget;
                    const isCurrent = entry.isCurrentWeek;
                    // Current week: solid primary; completed on-target: solid green; completed below: lighter green
                    const fill = isCurrent
                      ? '#22c55e'
                      : onTarget
                      ? '#22c55e'
                      : '#22c55e66'; // 40% opacity green for below-target
                    return <Cell key={`cell-${index}`} fill={fill} />;
                  })}
                </Bar>

                {/* Shadow target reference lines — one per week as a dashed horizontal segment */}
                {weeklyVolume.map((entry) => (
                  <ReferenceLine
                    key={`shadow-${entry.week}`}
                    x={entry.label}
                    y={entry.shadowTarget}
                    stroke="rgba(255,255,255,0.35)"
                    strokeDasharray="4 3"
                    strokeWidth={1.5}
                    label={{
                      value: entry.volumeLbs >= entry.shadowTarget ? '✓' : '',
                      position: 'insideTopRight',
                      fill: '#22c55e',
                      fontSize: 10,
                    }}
                  />
                ))}
              </ComposedChart>
            </ResponsiveContainer>
          </div>
        )}

        {/* Shadow status coaching line */}
        <div className={`flex items-start gap-2 px-3 py-2 rounded-sm border text-xs font-mono ${
          shadowStatus === 'ahead'
            ? 'bg-green-500/10 border-green-500/30 text-green-400'
            : shadowStatus === 'behind'
            ? 'bg-amber-500/10 border-amber-500/30 text-amber-400'
            : 'bg-primary/5 border-primary/20 text-primary'
        }`}>
          <span className="font-bold uppercase shrink-0">
            {shadowStatus === 'ahead' ? 'AHEAD' : shadowStatus === 'behind' ? 'BEHIND' : 'ON TRACK'}
          </span>
          <span className="text-muted-foreground font-normal">{shadowNote}</span>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-4 text-[9px] font-mono text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#22c55e]" />
            <span>On/above target</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-[#22c55e66]" />
            <span>Below target</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-4 border-t border-dashed border-white/40" />
            <span>Shadow target</span>
          </div>
        </div>
      </div>

      {/* ── 3. Sets Per Muscle Group ──────────────────────────────────────────── */}
      <div className="forge-panel p-4 space-y-3">
        <div className="forge-header">
          <span>Sets Per Muscle Group</span>
          <span className="text-primary font-mono flex items-center gap-1 text-xs">
            <Zap className="h-3.5 w-3.5" /> THIS BLOCK
          </span>
        </div>

        <div className="text-[10px] font-mono text-muted-foreground uppercase">Working Sets Accumulated</div>

        {setsPerMuscle.every((m) => m.sets === 0) ? (
          <div className="py-6 text-center border border-dashed border-border/40 rounded-sm">
            <p className="text-xs font-mono text-muted-foreground uppercase">No sets logged this block yet</p>
          </div>
        ) : (
          <div className="h-[180px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={setsPerMuscle}
                layout="vertical"
                margin={{ top: 0, right: 8, left: 4, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="muscle"
                  width={64}
                  tick={{ fontSize: 9, fontFamily: 'monospace', fill: 'hsl(var(--muted-foreground))' }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<MuscleTooltip />} cursor={{ fill: 'rgba(255,255,255,0.04)' }} />
                <Bar dataKey="sets" radius={[0, 2, 2, 0]}>
                  {setsPerMuscle.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={MUSCLE_COLORS[entry.muscle] ?? 'hsl(var(--primary))'}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* ── 4. Plateau Cards ─────────────────────────────────────────────────── */}
      {plateauCards.length > 0 && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-xs font-mono text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
              <ShieldAlert className="h-4 w-4" /> Plateau Alerts
            </span>
            <span className="font-mono text-[10px] bg-amber-500/10 text-amber-400 border border-amber-500/30 px-1.5 py-0.5 rounded-sm">
              {plateauCards.length} STALLED
            </span>
          </div>

          <div className="space-y-3">
            {plateauCards.map((pl) => (
              <div
                key={pl.exerciseId}
                className="forge-panel p-4 border-l-2 border-l-amber-500/60 space-y-3"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <button
                      className="flex items-center gap-1.5 text-left group"
                      onClick={() => {
                        setProgressionExerciseId(pl.exerciseId);
                        setProgressionExerciseName(pl.exerciseName);
                        setProgressionInitialMode('overload');
                      }}
                    >
                      <h4 className="font-bold text-sm text-foreground group-hover:text-primary transition-colors">
                        {pl.exerciseName}
                      </h4>
                      <LineChart className="h-3.5 w-3.5 text-muted-foreground/40 group-hover:text-primary transition-colors shrink-0" />
                    </button>
                    <p className="text-[9px] font-mono text-muted-foreground uppercase mt-0.5">{pl.category}</p>
                  </div>
                  <span className="text-[9px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 px-1.5 py-0.5 rounded-sm uppercase font-bold shrink-0">
                    STALLED {pl.stalledSessions} SESSIONS
                  </span>
                </div>

                <div className="flex justify-between text-xs font-mono bg-secondary/20 px-3 py-2 rounded-sm">
                  <span className="text-muted-foreground uppercase text-[10px]">Last Logged Best:</span>
                  <span className="text-foreground font-bold">{pl.lastWeight} LBS × {pl.lastReps} REPS</span>
                </div>

                <div className="bg-amber-500/5 border border-amber-500/20 p-3 rounded-sm">
                  <span className="text-[9px] font-mono text-amber-400 uppercase font-bold flex items-center gap-1 mb-1">
                    <ChevronRight className="h-3 w-3" /> Prescription
                  </span>
                  <p className="text-xs text-muted-foreground leading-relaxed">{pl.prescription}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── 5. Proactive Fatigue Status ──────────────────────────────────────── */}
      <div className={`forge-panel p-4 border ${fatigueColor} space-y-2`}>
        <div className="flex items-center gap-3">
          {fatigueIcon}
          <div className="min-w-0">
            <div className="font-mono font-bold text-sm uppercase tracking-wide">{fatigueTitle}</div>
            <div className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{fatigueBody}</div>
          </div>
        </div>
      </div>

      {/* ── 6. Workout Blueprints (collapsible) ──────────────────────────────── */}
      <div className="forge-panel overflow-hidden">
        <button
          className="w-full flex items-center justify-between p-4 hover:bg-secondary/20 transition-colors"
          onClick={() => setBlueprintsOpen(!blueprintsOpen)}
        >
          <div className="flex items-center gap-2">
            <ClipboardList className="h-4 w-4 text-primary" />
            <span className="font-mono text-sm font-bold uppercase tracking-wide">Workout Blueprints</span>
            {templates.length > 0 && (
              <span className="font-mono text-[10px] bg-primary/10 text-primary border border-primary/20 px-1.5 py-0.5 rounded-sm">
                {templates.length}
              </span>
            )}
          </div>
          {blueprintsOpen
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />
          }
        </button>

        {blueprintsOpen && (
          <div className="px-4 pb-4 space-y-4 border-t border-border/30">
            <div className="flex justify-between items-center pt-3">
              <span className="text-xs font-mono text-muted-foreground uppercase">Saved Routines</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setIsCreatingTemplate(!isCreatingTemplate)}
                className="font-mono text-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground h-7"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Create New
              </Button>
            </div>

            {isCreatingTemplate && (
              <form onSubmit={handleCreateTemplate} className="p-3 bg-secondary/50 border border-primary/30 rounded-sm space-y-3">
                <div className="text-xs font-mono text-primary uppercase tracking-wider">Configure Blueprint</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-mono text-muted-foreground uppercase mb-1">Routine Name</label>
                    <Input
                      placeholder="e.g. Push Day Heavy"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      className="bg-background border-border text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-mono text-muted-foreground uppercase mb-1">Category</label>
                    <select
                      value={newTemplateCategory}
                      onChange={(e) => setNewTemplateCategory(e.target.value as any)}
                      className="w-full h-9 bg-background border border-border rounded-md px-3 text-sm font-mono text-primary focus:outline-none focus:ring-1 focus:ring-primary"
                    >
                      <option value="Push">PUSH</option>
                      <option value="Pull">PULL</option>
                      <option value="Legs">LEGS</option>
                      <option value="Core/Cardio">CORE/CARDIO</option>
                      <option value="Other">OTHER</option>
                    </select>
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="block text-xs font-mono text-muted-foreground uppercase">Select Lifts & Sets</label>
                  <div className="max-h-[150px] overflow-y-auto border border-border/60 rounded-sm p-2 space-y-1 bg-background/30">
                    {exercises.map((ex) => {
                      const isSelected = selectedLifts.some((l) => l.exerciseId === ex.id);
                      const currentSets = selectedLifts.find((l) => l.exerciseId === ex.id)?.targetSetsCount || tempSetsCount[ex.id] || 3;
                      return (
                        <div key={ex.id} className="flex items-center justify-between text-xs p-1 hover:bg-secondary/40 rounded-sm">
                          <button
                            type="button"
                            onClick={() => handleToggleLift(ex.id)}
                            className={`flex items-center gap-2 text-left flex-1 min-w-0 font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}
                          >
                            <span className={`w-3 h-3 border rounded-sm flex items-center justify-center ${isSelected ? 'border-primary bg-primary/20' : 'border-border'}`}>
                              {isSelected && <Check className="h-2 w-2 text-primary" />}
                            </span>
                            <span className="truncate">{ex.name}</span>
                          </button>
                          {isSelected && (
                            <div className="flex items-center gap-1.5 ml-2">
                              <span className="text-[10px] font-mono text-muted-foreground uppercase">Sets:</span>
                              <Input
                                type="number"
                                min="1"
                                max="10"
                                value={currentSets}
                                onChange={(e) => handleSetsChange(ex.id, parseInt(e.target.value) || 3)}
                                className="w-12 h-6 bg-secondary border-border text-center font-mono text-xs"
                              />
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-1">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setIsCreatingTemplate(false)} className="font-mono text-xs">
                    Cancel
                  </Button>
                  <Button type="submit" size="sm" disabled={saveTemplateMutation.isPending} className="bg-primary text-primary-foreground font-mono text-xs">
                    Save Blueprint
                  </Button>
                </div>
              </form>
            )}

            {templates.length === 0 ? (
              <div className="text-center py-6 border border-dashed border-border/50 rounded-sm font-mono text-xs text-muted-foreground uppercase">
                No Saved Blueprints
              </div>
            ) : (
              <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                {['Push', 'Pull', 'Legs', 'Core/Cardio', 'Other'].map((cat) => {
                  const catTemplates = templates.filter((t) => (t.category || 'Other') === cat);
                  if (catTemplates.length === 0) return null;
                  return (
                    <div key={cat} className="space-y-1.5">
                      <div className="text-[10px] font-mono font-bold text-primary/70 tracking-wider uppercase border-b border-border/30 pb-0.5">
                        {cat} Routines
                      </div>
                      {catTemplates.map((t) => (
                        <div key={t.id} className="p-3 bg-secondary/30 border border-border/60 rounded-sm flex items-center justify-between hover:border-primary/40 transition-colors">
                          <div className="min-w-0">
                            <h4 className="font-bold text-sm text-primary">{t.name}</h4>
                            <p className="text-[10px] font-mono text-muted-foreground uppercase mt-0.5">
                              {t.exercises.length} Exercises · {t.exercises.reduce((acc: number, curr: any) => acc + curr.targetSetsCount, 0)} Sets
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {onLoadTemplate && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => onLoadTemplate(t)}
                                className="h-7 px-2 font-mono text-xs border-primary text-primary hover:bg-primary hover:text-primary-foreground"
                              >
                                Load
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDeleteTemplate(t.id, t.name)}
                              className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Exercise Progression Dialog */}
      <ExerciseProgressionDialog
        exerciseId={progressionExerciseId}
        exerciseName={progressionExerciseName}
        initialMode={progressionInitialMode}
        onClose={() => setProgressionExerciseId(null)}
      />
    </div>
  );
}
